import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";

import Radio from "../../../../components/mui-mod/Radio";
import Checkbox from "../../../../components/mui-mod/Checkbox";

import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { DateTimePicker } from "@mui/x-date-pickers";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

export const CustomField = ({
    customField,
    styles,
    onChange,
    value,
    error,
    errorMessage,
}) => {
    const { t } = useTranslation();

    const { label, type, options, id } = customField;
    const { field_style, font_color, font_size, font_family } = styles;

    let name = `custom_field[${String(id)}]`;

    const customFieldType = {
        radio: (
            <FormControl>
                <FormLabel
                    id="demo-row-radio-buttons-group-label"
                    style={{
                        color: font_color,
                        fontSize: `${font_size || "14"}px`,
                        fontFamily: font_family,
                    }}
                >
                    {label}
                </FormLabel>
                <RadioGroup
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name={name}
                    value={value}
                    onChange={onChange}
                >
                    {(options && options.length) > 0
                        ? options.map((option, index) => (
                              <FormControlLabel
                                  key={index}
                                  value={option}
                                  control={<Radio color="default" />}
                                  label={option}
                                  style={{
                                      color: font_color,
                                  }}
                                  componentsProps={{
                                      typography: {
                                          fontSize: `${
                                              font_size - 2 || "14"
                                          }px`,
                                          fontFamily: font_family,
                                      },
                                  }}
                              />
                          ))
                        : ""}
                </RadioGroup>
                <FormHelperText error={error}>
                    {error && errorMessage}
                </FormHelperText>
            </FormControl>
        ),
        checkbox: (
            <FormControl>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                >
                    <FormControlLabel
                        label={label}
                        control={
                            <Checkbox
                                onChange={onChange}
                                name={name}
                                checked={value}
                                color="default"
                            />
                        }
                        style={{
                            color: font_color,
                        }}
                        componentsProps={{
                            typography: {
                                fontSize: `${font_size || "14"}px`,
                                fontFamily: font_family,
                            },
                        }}
                    />
                </RadioGroup>
                <FormHelperText error={error}>
                    {error && errorMessage}
                </FormHelperText>
            </FormControl>
        ),
        select: (
            <FormControl variant="filled" sx={{ margin: 0 }} fullWidth>
                <InputLabel
                    id="demo-simple-select-filled-label"
                    style={{
                        color: font_color,
                        fontSize: `${font_size || "14"}px`,
                        fontFamily: font_family,
                    }}
                >
                    {label}
                </InputLabel>
                <Select
                    labelId="demo-simple-select-filled-label"
                    id="demo-simple-select-filled"
                    defaultValue={""}
                    name={name}
                    value={value}
                    onChange={onChange}
                    componentsProps={{
                        root: {
                            style: {
                                borderRadius:
                                    field_style === "square" ? "0" : "8px",
                                backgroundColor: "#fff",
                                boxShadow: "none",
                                borderColor: "#E0E2E4",
                            },
                        },
                    }}
                >
                    <MenuItem value="">
                        <em>{t("Selecione")}</em>
                    </MenuItem>
                    {(options && options.length) > 0
                        ? options.map((option, index) => (
                              <MenuItem key={index} value={option}>
                                  {option}
                              </MenuItem>
                          ))
                        : ""}
                </Select>
                <FormHelperText error={error}>
                    {error && errorMessage}
                </FormHelperText>
            </FormControl>
        ),
        multiselect: (
            <FormControl variant="filled" sx={{ margin: 0 }} fullWidth>
                <InputLabel
                    id="demo-simple-select-filled-label"
                    style={{
                        color: font_color,
                        fontSize: `${font_size || "14"}px`,
                        fontFamily: font_family,
                    }}
                >
                    {label}
                </InputLabel>
                <Select
                    labelId="demo-simple-select-filled-label"
                    id="demo-simple-select-filled"
                    multiple
                    name={name}
                    value={value || []}
                    onChange={onChange}
                    componentsProps={{
                        root: {
                            style: {
                                borderRadius:
                                    field_style === "square" ? "0" : "8px",
                                backgroundColor: "#fff",
                                boxShadow: "none",
                                borderColor: "#E0E2E4",
                            },
                        },
                    }}
                >
                    {(options && options.length) > 0
                        ? options.map((option, index) => (
                              <MenuItem key={index} value={option}>
                                  {option}
                              </MenuItem>
                          ))
                        : ""}
                </Select>
                <FormHelperText error={error}>
                    {error && errorMessage}
                </FormHelperText>
            </FormControl>
        ),
        date: (
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                    format={moment.locale() === "en" ? "MM/DD/YYYY HH:mm:ss" : "DD/MM/YYYY HH:mm:ss"}
                    fullWidth
                    onChange={(date) => {
                        const formattedDate = moment(date).format(
                            "YYYY-MM-DD HH:mm:ss"
                        );
                        onChange({ target: { name, value: formattedDate } });
                    }}
                    value={moment(value)}
                    name={name}
                    label={label}
                    slotProps={{
                        textField: {
                            variant: "filled",
                            error: error,
                            helperText: error && errorMessage,
                            fullWidth: true,
                            InputLabelProps: {
                                style: {
                                    color: font_color,
                                    fontSize: `${font_size || "14"}px`,
                                    fontFamily: font_family,
                                },
                            },
                            InputProps: {
                                style: {
                                    borderRadius:
                                        field_style === "square" ? "0" : "8px",
                                    backgroundColor: "#fff",
                                    boxShadow: "none",
                                    borderColor: "#E0E2E4",
                                },
                            },
                        },
                    }}
                    viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock,
                    }}
                />
            </LocalizationProvider>
        ),
        number: (
            <TextField
                id={label}
                label={label}
                fullWidth
                name={name}
                value={value}
                onChange={onChange}
                size="small"
                type="number"
                variant="filled"
                error={error}
                helperText={error && errorMessage}
                InputLabelProps={{
                    style: {
                        color: font_color,
                        fontSize: `${font_size || "14"}px`,
                        fontFamily: font_family,
                    },
                }}
                InputProps={{
                    style: {
                        borderRadius: field_style === "square" ? "0" : "8px",
                        backgroundColor: "#fff",
                        boxShadow: "none",
                        borderColor: "#E0E2E4",
                    },
                }}
            />
        ),
        text: (
            <TextField
                name={name}
                id={label}
                label={label}
                fullWidth
                value={value}
                size="small"
                variant="filled"
                onChange={onChange}
                error={error}
                helperText={error && errorMessage}
                InputLabelProps={{
                    style: {
                        color: font_color,
                        fontSize: `${font_size || "14"}px`,
                        fontFamily: font_family,
                    },
                }}
                InputProps={{
                    style: {
                        borderRadius: field_style === "square" ? "0" : "8px",
                        backgroundColor: "#fff",
                        boxShadow: "none",
                        borderColor: "#E0E2E4",
                    },
                }}
            />
        ),
        textarea: (
            <TextField
                id={label}
                label={label}
                name={name}
                value={value}
                onChange={onChange}
                fullWidth
                multiline
                rows={4}
                variant="filled"
                error={error}
                helperText={error && errorMessage}
                InputLabelProps={{
                    style: {
                        color: font_color,
                        fontSize: `${font_size || "14"}px`,
                        fontFamily: font_family,
                    },
                }}
                InputProps={{
                    style: {
                        borderRadius: field_style === "square" ? "0" : "8px",
                        backgroundColor: "#fff",
                        boxShadow: "none",
                        borderColor: "#E0E2E4",
                    },
                }}
            />
        ),
    };

    return customFieldType[type] || null;
};

export default CustomField;
